// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Ask Helios conversation history (ADR-0009, G4.4).
 *
 * Lists the caller's stored conversations grouped by bound context, so an
 * operator mid-incident finds "the thread I was on for ALM-2107" rather than
 * scanning a flat reverse-chronological list.
 *
 * Server-side the list is owner-scoped, so nothing here needs to filter by user.
 * A 503 means conversation history is not provisioned in this environment, which
 * is deliberately distinguished from "you have no conversations" — the former
 * would otherwise read as data loss.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from '@carbon/icons-react';
import {
  archiveConversation,
  listConversations,
  renameConversation,
} from '../../services/askHeliosService';
import type { ConversationSummary } from '../../types';
import { useSiteTime } from '../../hooks/useSiteTime';
import type { SiteTimeValue } from '../../contexts/SiteTimeContext';
import './ConversationHistory.css';

/** Per-item Rename/Archive actions, hidden for now. Logic stays wired up
 *  (handleArchive/submitRename, rename-form state) for a later re-enable. */
const SHOW_ITEM_ACTIONS = false;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "2h ago" / "Yesterday" / "2 days ago" / a short date once it's stale. */
function formatRelativeTime(
  iso: string | null | undefined,
  format: SiteTimeValue['format']
): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < HOUR) return `${Math.max(1, Math.round(diff / MINUTE))}m ago`;
  if (diff < DAY) return `${Math.round(diff / HOUR)}h ago`;
  const days = Math.round(diff / DAY);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return format(iso, 'dateShort', '');
}

/** Subtitle line: the chips a thread is bound to, or the `contextLabel` fallback. */
function contextSubtitle(conversation: ConversationSummary): string {
  if (conversation.context_chips?.length) {
    return conversation.context_chips.map(chip => chip.label).join(' · ');
  }
  return contextLabel(conversation.context_ref);
}

export interface ConversationHistoryProps {
  /** Currently open conversation, highlighted in the list. */
  activeConversationId?: string;
  /** Resume a conversation — replaces the on-screen thread. */
  onResume: (_conversationId: string) => void;
  /**
   * Called when the archived conversation was the one on screen, so the caller
   * can reset the thread. Without it the store keeps pointing at an archived
   * record and the next question is appended to it.
   */
  onArchivedActive?: () => void;
  /** Restrict the list to one bound context, e.g. `alarm:ALM-2107`. */
  contextRef?: string;
}

/** Human label for the `contextRef` a conversation is bound to. */
function contextLabel(contextRef: string): string {
  if (!contextRef || contextRef === 'global') return 'Ambient · whole site';
  const [kind, ...rest] = contextRef.split(':');
  const value = rest.join(':');
  if (kind === 'alarm') return `Alarm ${value}`;
  if (kind === 'asset') return `Asset ${value}`;
  if (kind === 'site') return value;
  return contextRef;
}

export default function ConversationHistory({
  activeConversationId,
  onResume,
  onArchivedActive,
  contextRef,
}: ConversationHistoryProps) {
  const { format } = useSiteTime();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [search, setSearch] = useState('');

  const visibleConversations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter(
      c =>
        c.title.toLowerCase().includes(query) ||
        c.last_message_preview.toLowerCase().includes(query)
    );
  }, [conversations, search]);

  // `keepError` is what makes a failed archive or rename visible. Both handlers
  // set an error and then reload, and an unconditional `setError(null)` here
  // cleared it before it could ever render — while `isLoading` also replaced the
  // whole tree with the spinner, so the `role="alert"` never mounted at all. The
  // row silently reappeared and the operation looked like it had succeeded.
  const load = useCallback(
    async (keepError = false) => {
      setIsLoading(true);
      if (!keepError) setError(null);
      try {
        const response = await listConversations({ contextRef, status: 'active' });
        setConversations(response.conversations);
        setUnavailable(false);
      } catch (err) {
        if ((err as { status?: number }).status === 503) {
          setUnavailable(true);
        } else {
          setError('Conversation history could not be loaded.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [contextRef]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const handleArchive = useCallback(
    async (conversationId: string) => {
      // Optimistic: the row leaves the active list immediately, and a failure
      // restores it by reloading rather than by guessing at prior state.
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
      try {
        await archiveConversation(conversationId);
        // Archiving the thread that is currently on screen has to reset it.
        // Without this the store kept `conversationId` pointing at the archived
        // record, so the operator watched the conversation leave the list and
        // then had their next question appended to it — writing into a
        // soft-deleted record with a pinned retention TTL. Same class of bug as
        // the resume-failure id leak, on the delete path.
        if (conversationId === activeConversationId) onArchivedActive?.();
      } catch {
        setError('That conversation could not be archived.');
        void load(true);
      }
    },
    [load, activeConversationId, onArchivedActive]
  );

  const submitRename = useCallback(
    async (conversationId: string) => {
      const title = renameValue.trim();
      setRenamingId(null);
      if (!title) return;

      setConversations(prev =>
        prev.map(c => (c.conversation_id === conversationId ? { ...c, title } : c))
      );
      try {
        await renameConversation(conversationId, title);
      } catch {
        setError('That conversation could not be renamed.');
        void load(true);
      }
    },
    [renameValue, load]
  );

  // Rendered above every early return, so a failure that triggers a reload is
  // still announced while the list is refetching.
  const errorBanner = error ? (
    <p className="akh-history-error" role="alert">
      {error}
    </p>
  ) : null;

  const searchBar = (
    <div className="akh-history-search">
      <Search size={16} className="akh-history-search-icon" />
      <input
        type="text"
        className="akh-history-search-input"
        placeholder="Search past conversations..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        aria-label="Search past conversations"
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="akh-history">
        {searchBar}
        <div className="akh-history-empty" aria-busy="true">
          {errorBanner}
          Loading conversations…
        </div>
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="akh-history">
        {searchBar}
        <div className="akh-history-empty">
          Past conversations are unavailable right now. Try again later.
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="akh-history">
        {searchBar}
        <div className="akh-history-empty">
          {errorBanner}
          No saved conversations yet. Ask a question to start one.
        </div>
      </div>
    );
  }

  return (
    <div className="akh-history">
      {searchBar}
      {errorBanner}

      {visibleConversations.length === 0 ? (
        <div className="akh-history-empty">No conversations match “{search}”.</div>
      ) : (
        <ul className="akh-history-list">
          {visibleConversations.map(conversation => {
            const isActive = conversation.conversation_id === activeConversationId;
            const isRenaming = renamingId === conversation.conversation_id;

            return (
              <li
                key={conversation.conversation_id}
                className={`akh-history-item${isActive ? ' akh-history-item-active' : ''}`}
              >
                {isRenaming ? (
                  <form
                    className="akh-history-rename"
                    onSubmit={e => {
                      e.preventDefault();
                      void submitRename(conversation.conversation_id);
                    }}
                  >
                    <label
                      className="akh-history-rename-label"
                      htmlFor={`rename-${conversation.conversation_id}`}
                    >
                      Conversation title
                    </label>
                    <input
                      id={`rename-${conversation.conversation_id}`}
                      className="akh-history-rename-input"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={() => void submitRename(conversation.conversation_id)}
                      autoFocus
                    />
                  </form>
                ) : (
                  <button
                    type="button"
                    className="akh-history-open"
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() => onResume(conversation.conversation_id)}
                  >
                    <span className="akh-history-item-head">
                      <span className="akh-history-title">
                        {conversation.title || 'Untitled conversation'}
                      </span>
                      <span className="akh-history-time">
                        {formatRelativeTime(
                          conversation.updated_at ?? conversation.created_at,
                          format
                        )}
                      </span>
                    </span>
                    <span className="akh-history-subtitle">{contextSubtitle(conversation)}</span>
                    {conversation.last_message_preview && (
                      <span className="akh-history-preview">
                        {conversation.last_message_preview}
                      </span>
                    )}
                  </button>
                )}

                {SHOW_ITEM_ACTIONS && (
                  <div className="akh-history-actions">
                    <button
                      type="button"
                      className="akh-history-action"
                      onClick={() => {
                        setRenamingId(conversation.conversation_id);
                        setRenameValue(conversation.title || '');
                      }}
                      aria-label={`Rename ${conversation.title || 'conversation'}`}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="akh-history-action"
                      onClick={() => void handleArchive(conversation.conversation_id)}
                      aria-label={`Archive ${conversation.title || 'conversation'}`}
                    >
                      Archive
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
